"use client";

import { Toast as ToastPrimitive } from "@base-ui/react/toast";
import { CircleCheck, CircleX, Info, TriangleAlert, X } from "lucide-react";
import type * as React from "react";
import { IconButton } from "@/components/ui/molecules/icon-button";
import { cn } from "@/lib/utils";

const DEFAULT_TIMEOUT_MS = 4000;
const DEFAULT_LIMIT = 3;

export type ToastType = "success" | "error" | "warning" | "info";

export type ToastOptions = {
  description?: React.ReactNode;
  /**
   * Showing a toast with an id that is already on screen replaces it in place
   * and restarts its timer instead of stacking a second one.
   */
  id?: string;
  /** Auto-dismiss delay in ms. `0` keeps the toast open until it is closed. */
  duration?: number;
};

type ShowToast = (title: React.ReactNode, options?: ToastOptions) => string;

export type ToastApi = ShowToast & {
  success: ShowToast;
  error: ShowToast;
  warning: ShowToast;
  info: ShowToast;
};

export type ToastManager = ReturnType<typeof ToastPrimitive.createToastManager>;

/** Creates an isolated manager, e.g. for a story or test with its own `Toaster`. */
export const createToastManager = (): ToastManager =>
  ToastPrimitive.createToastManager();

/**
 * Builds the imperative `toast` API over a manager. The methods are plain
 * properties, so tests can `spyOn(toast, "error")`.
 */
export function createToast(manager: ToastManager): ToastApi {
  const show =
    (type?: ToastType): ShowToast =>
    (title, { description, id, duration } = {}) =>
      // `description` and `timeout` are always passed, even when undefined:
      // re-adding an existing id merges into the toast on screen, and an
      // omitted key would keep the previous toast's value.
      manager.add({
        id,
        title,
        description,
        timeout: duration,
        type,
        priority: type === "error" ? "high" : "low",
      });

  return Object.assign(show(), {
    success: show("success"),
    error: show("error"),
    warning: show("warning"),
    info: show("info"),
  });
}

const defaultManager = createToastManager();

/**
 * App-wide toasts, callable from anywhere — React Query callbacks and other
 * non-React modules included. Rendered by the app's single `<Toaster />`.
 */
export const toast = createToast(defaultManager);

export type ToasterProps = {
  /** Defaults to the manager behind `toast`; pass one from `createToastManager()` to isolate. */
  toastManager?: ToastManager;
  /** Auto-dismiss delay for toasts that don't set `duration`. @default 4000 */
  timeout?: number;
  /** Toasts kept in the stack; newer ones push older ones out. @default 3 */
  limit?: number;
};

/**
 * Renders toasts at the top centre of the page. Mount exactly one per app
 * for a given manager — every `Toaster` on a manager renders each toast.
 *
 * The manager does not queue: a toast shown before this component has
 * mounted is dropped. Render it ahead of anything that can toast on mount,
 * as a sibling rather than a wrapper.
 */
export function Toaster({
  toastManager = defaultManager,
  timeout = DEFAULT_TIMEOUT_MS,
  limit = DEFAULT_LIMIT,
}: ToasterProps) {
  return (
    <ToastPrimitive.Provider
      limit={limit}
      timeout={timeout}
      toastManager={toastManager}
    >
      {/* Modal dialogs aria-hide everything outside them except [aria-live]
          subtrees. Base UI announces errors from a hidden sibling of the
          viewport, so the whole portal opts in to stay audible. "off" keeps
          this wrapper itself silent; the viewport and alerts set their own. */}
      <ToastPrimitive.Portal aria-live="off">
        <ToastPrimitive.Viewport className="pointer-events-none fixed inset-x-0 top-4 z-[60] mx-auto w-[min(calc(100vw-2rem),24rem)] outline-none">
          <ToastList />
        </ToastPrimitive.Viewport>
      </ToastPrimitive.Portal>
    </ToastPrimitive.Provider>
  );
}

const TYPE_ICONS: Record<
  ToastType,
  React.ComponentType<{ className?: string }>
> = {
  success: CircleCheck,
  error: CircleX,
  warning: TriangleAlert,
  info: Info,
};

const isToastType = (type: string | undefined): type is ToastType =>
  type !== undefined && Object.hasOwn(TYPE_ICONS, type);

function ToastList() {
  const { toasts } = ToastPrimitive.useToastManager();

  return toasts.map((item) => {
    const Icon = isToastType(item.type) ? TYPE_ICONS[item.type] : null;
    return (
      <ToastPrimitive.Root
        className={cn(
          "group pointer-events-auto absolute inset-x-0 top-0 rounded-lg border border-border bg-popover text-popover-foreground text-sm shadow-[var(--shadow-card-hover)]",
          // Collapsed, every toast takes the front toast's height and peeks out
          // below it, scaled down a little per step back. Hovering or focusing
          // the stack (data-expanded) lays the toasts out at their own heights.
          "[--gap:0.5rem] [--height:var(--toast-frontmost-height,var(--toast-height))] [--peek:0.625rem] [--scale:calc(max(0,1-var(--toast-index)*0.05))] [--shrink:calc(1-var(--scale))]",
          "z-[calc(1000-var(--toast-index))] h-[var(--height)] origin-top [transform:translateY(calc(var(--toast-swipe-movement-y)+var(--toast-index)*var(--peek)+var(--shrink)*var(--height)))_scale(var(--scale))]",
          "data-[expanded]:h-[var(--toast-height)] data-[expanded]:[transform:translateY(calc(var(--toast-offset-y)+var(--toast-index)*var(--gap)+var(--toast-swipe-movement-y)))]",
          // Fills the gap below each expanded toast, so moving the pointer
          // between two toasts doesn't collapse the stack.
          "data-[expanded]:after:absolute data-[expanded]:after:inset-x-0 data-[expanded]:after:top-full data-[expanded]:after:h-[calc(var(--gap)+1px)] data-[expanded]:after:content-['']",
          "[transition:transform_var(--duration-slow)_var(--ease-smooth),opacity_var(--duration-slow),height_var(--duration-fast)] data-[swiping]:transition-none motion-reduce:transition-none",
          "data-[ending-style]:opacity-0 data-[limited]:opacity-0 data-[starting-style]:opacity-0 data-[starting-style]:[transform:translateY(-0.5rem)]",
          "data-[ending-style]:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]",
          // The tint is mixed into the popover colour rather than layered at
          // low opacity, so page content never shows through the toast.
          "data-[type=success]:border-[color-mix(in_oklab,var(--success)_40%,var(--border))] data-[type=success]:bg-[color-mix(in_oklab,var(--success)_10%,var(--popover))]",
          "data-[type=error]:border-[color-mix(in_oklab,var(--destructive)_40%,var(--border))] data-[type=error]:bg-[color-mix(in_oklab,var(--destructive)_10%,var(--popover))]",
          "data-[type=warning]:border-[color-mix(in_oklab,var(--warning)_50%,var(--border))] data-[type=warning]:bg-[color-mix(in_oklab,var(--warning)_12%,var(--popover))]"
        )}
        data-slot="toast"
        key={item.id}
        swipeDirection="up"
        toast={item}
      >
        {/* Behind the front toast, the copy is hidden so only the edge of
            each card shows; it fades back in when the stack expands. */}
        <ToastPrimitive.Content className="flex h-full items-start gap-2.5 overflow-hidden py-3 pr-2 pl-3 transition-opacity duration-[var(--duration-slow)] data-[behind]:opacity-0 data-[expanded]:opacity-100 motion-reduce:transition-none">
          {Icon && (
            <Icon
              aria-hidden
              className={cn(
                "mt-0.5 size-4 shrink-0 text-muted-foreground",
                // The status tokens alone fall under 3:1 on their own light tint,
                // so icons are deepened toward the foreground colour.
                "group-data-[type=error]:text-[color-mix(in_oklab,var(--destructive)_75%,var(--foreground))] group-data-[type=success]:text-[color-mix(in_oklab,var(--success)_75%,var(--foreground))] group-data-[type=warning]:text-[color-mix(in_oklab,var(--warning)_75%,var(--foreground))]"
              )}
            />
          )}
          {/* Copy stays in neutral colours: the status tokens are too light for
              small text, and muted-foreground falls under 4.5:1 on the tints. */}
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <ToastPrimitive.Title
              className="font-medium text-foreground leading-5"
              data-slot="toast-title"
            />
            <ToastPrimitive.Description
              className="text-[color-mix(in_oklab,var(--foreground)_75%,var(--popover))]"
              data-slot="toast-description"
            />
          </div>
          <IconButton
            className="-my-1 shrink-0"
            render={<ToastPrimitive.Close />}
            size="xs"
            tooltipText="Dismiss"
            variant="ghost"
          >
            <X />
          </IconButton>
        </ToastPrimitive.Content>
      </ToastPrimitive.Root>
    );
  });
}
